import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faDollarSign,
  faLayerGroup,
  faChartLine,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import Card from "../ui/Card";
import "../../style/mfca-dashboard.css";

// reusable info tooltip — same pattern as DonorGHG/SankeyPanel
const InfoTip = ({ text, placement = "top" }) => (
  <Tooltip title={text} arrow placement={placement}>
    <span style={{ cursor: "help", opacity: 0.45, fontSize: "0.65em", marginLeft: 4 }}>
      <FontAwesomeIcon icon={faCircleInfo} />
    </span>
  </Tooltip>
);

export { InfoTip };

const getBaseline = (acqCost, refPrice, listingCost) => acqCost || refPrice || listingCost || 0;

const DonorMFCASummary = ({
  transactions = [],
  expiredListings = [],
  darkMode = false,
  isRefreshing = false,
  justUpdated = false,
}) => {
  const { t } = useTranslation();

  const mfca = useMemo(() => {
    let totalValue = 0;
    let recoveredValue = 0;

    for (const txn of transactions) {
      const baseline = getBaseline(txn.acquisitionCost, txn.referencePricePerLb, txn.listingCost);
      const qty = txn.quantity || 0;
      const accepted = txn.quantityAccepted != null ? txn.quantityAccepted : qty;

      totalValue += baseline * qty;
      recoveredValue += baseline * accepted;
    }

    let expiredValue = 0;
    for (const exp of expiredListings) {
      const baseline = getBaseline(exp.acquisitionCost, exp.referencePricePerLb, exp.cost);
      expiredValue += baseline * (exp.remainingQty || 0);
    }

    totalValue += expiredValue;
    const opportunityValue = totalValue - recoveredValue;
    const diversionRate = totalValue > 0 ? (recoveredValue / totalValue) * 100 : 0;

    return { totalValue, recoveredValue, opportunityValue, diversionRate };
  }, [transactions, expiredListings]);

  const hasData = mfca.totalValue > 0 || expiredListings.length > 0;
  if (!hasData) return null;

  const fmt = (v) =>
    `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const cards = [
    {
      icon: faBoxOpen,
      value: fmt(mfca.recoveredValue),
      title: t("Value Diverted from Landfill"),
      description: t("Matched & delivered via Punar"),
      colorClass: "counter-green",
      tip: t("The estimated value of surplus material that was successfully matched to a receiver and delivered — material that would otherwise have been sent to landfill or disposal. List more material and respond quickly to receiver interest to increase this number."),
    },
    {
      icon: faDollarSign,
      value: fmt(mfca.totalValue),
      title: t("Total Surplus Value"),
      description: `${mfca.diversionRate.toFixed(1)}% ${t("diverted")} · ${(100 - mfca.diversionRate).toFixed(1)}% ${t("unrealized")}`,
      colorClass: "counter-blue",
      tip: t("The total acquisition cost of all material you've listed on Punar — what you originally paid per unit, or market reference values when not provided. This is your baseline: the value that entered the Punar system."),
    },
    {
      icon: faLayerGroup,
      value: fmt(mfca.opportunityValue),
      title: t("Still on the Table"),
      description: t("Could be captured with better timing or matching"),
      colorClass: "counter-amber",
      tip: t("Value of material that expired before finding a match or was returned by a receiver. Material value only — system and labor costs aren't included here. This isn't a loss — it's value you could still capture by extending pickup deadlines, adjusting pricing, or improving material descriptions for the next listing."),
    },
    {
      icon: faChartLine,
      value: `${mfca.diversionRate.toFixed(1)}%`,
      title: t("Diversion Rate"),
      description: t("vs 0% without Punar"),
      colorClass: "counter-green",
      tip: t("The percentage of your surplus material value diverted from landfill through Punar. Without the platform, this would be 0% — everything goes to disposal. Even a 30% diversion rate represents significant cost savings compared to full disposal."),
    },
  ];

  return (
    <div className="mfca-summary-wrap">
      <p className="mfca-disclosure">
        {t("Calculated using Preliminary MFCA (ISO 14051) estimates based on marketplace averages.")}
      </p>
      <div className="counter-cards-container">
        {cards.map((card, i) => (
          <Card
            key={i}
            icon={card.icon}
            value={card.value}
            title={
              <span style={{ display: "flex", alignItems: "center" }}>
                {card.title}
                <InfoTip text={card.tip} />
              </span>
            }
            description={card.description}
            variant="counter"
            colorClass={card.colorClass}
            darkMode={darkMode}
            isLoading={isRefreshing}
            justUpdated={justUpdated}
          />
        ))}
      </div>
    </div>
  );
};

export default DonorMFCASummary;
